/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import Grid from "@mui/material/Grid";
import { formValue } from "../../../services/formServiceFunctions";
import MenuItem from "@mui/material/MenuItem";
import useForm from "../../../hooks/useForm";
import { requiredFieldDefault, textFieldDefault } from "../../../services/validationRules";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import ValidatorForm from "../../Validator/ValidatorForm";
import SubmitButton from "../../Button/SubmitButton";
import _ from "lodash";
import ValidatorNumberField from "../../Validator/ValidatorNumberField";
import { Box } from "@mui/material";
import { processCostSupportStyle } from "../processCostSupportStyle";
import { ProcessCostSupportFormProps } from "../../../types/ProcessCostSupport";

const IncomeOther = ({ pcs, onSubmit, isLoading }: ProcessCostSupportFormProps) => {
  const initialValues = {
    hasIncomeOther1: pcs.hasIncomeOther1,
    incomeOther1Name: pcs.incomeOther1Name,
    incomeOther1Amount: pcs.incomeOther1Amount,
    incomeOther1Period: pcs.incomeOther1Period,
    hasIncomeOther2: pcs.hasIncomeOther2,
    incomeOther2Name: pcs.incomeOther2Name,
    incomeOther2Amount: pcs.incomeOther2Amount,
    incomeOther2Period: pcs.incomeOther2Period,
  };

  const { values, errors, handleChange, handleSubmit, handleBlur, registerValidators, touchedValues } = useForm({
    initialValues,
    onSubmit,
    identifier: pcs,
  });

  return (
    <>
      <Box sx={processCostSupportStyle.sectionHeadline}>Einnahmen</Box>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container sx={processCostSupportStyle.grid}>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Haben Sie sonstige regelmäßige, unregelmäßige oder einmalige Einnahmen?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Einnahmen"}
              name={"hasIncomeOther1"}
              value={formValue(values, "hasIncomeOther1")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              dependentValidationFields={[
                "incomeOther1Name",
                "incomeOther1Amount",
                "incomeOther1Period",
                "hasIncomeOther2",
              ]}
            >
              <MenuItem value={"no"}>Nein</MenuItem>
              <MenuItem value={"yes"}>Ja</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              id="incomeOther1Name"
              name="incomeOther1Name"
              label="Beschreibung der Einnahmen"
              onBlur={handleBlur}
              registerValidators={registerValidators}
              validators={
                formValue(values, "hasIncomeOther1") === "yes" ? [...requiredFieldDefault, ...textFieldDefault] : []
              }
              error={!!errors["incomeOther1Name"]}
              helperText={errors["incomeOther1Name"]}
              margin="normal"
              value={formValue(values, "incomeOther1Name")}
              onChange={handleChange}
              disabled={formValue(values, "hasIncomeOther1") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="incomeOther1Amount"
              name="incomeOther1Amount"
              label="Bruttobetrag"
              onBlur={handleBlur}
              registerValidators={registerValidators}
              isHighlighted={formValue(values, "hasIncomeOther1") === "yes"}
              error={!!errors["incomeOther1Amount"]}
              helperText={errors["incomeOther1Amount"]}
              margin="normal"
              value={formValue(values, "incomeOther1Amount")}
              onChange={handleChange}
              disabled={formValue(values, "hasIncomeOther1") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Wann erhalten Sie die Einnahmen?"}
              onBlur={handleBlur}
              error={!!errors["incomeOther1Period"]}
              helperText={errors["incomeOther1Period"]}
              validators={formValue(values, "hasIncomeOther1") === "yes" ? requiredFieldDefault : []}
              fullWidth
              registerValidators={registerValidators}
              value={formValue(values, "incomeOther1Period")}
              onChange={handleChange}
              name={`incomeOther1Period`}
              inputProps={{
                name: `incomeOther1Period`,
                id: "incomeOther1Period",
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { maxHeight: 200 },
                  },
                },
              }}
              disabled={formValue(values, "hasIncomeOther1") !== "yes"}
            >
              <MenuItem value={"jährlich"}>jährlich</MenuItem>
              <MenuItem value={"quartalsweise"}>quartalsweise</MenuItem>
              <MenuItem value={"monatlich"}>monatlich</MenuItem>
              <MenuItem value={"einmalig"}>einmalig</MenuItem>
              <MenuItem value={"unregelmäßig"}>unregelmäßig</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Haben Sie noch weitere Einnahmen?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Weitere Einnahmen"}
              name={"hasIncomeOther2"}
              value={formValue(values, "hasIncomeOther2")}
              validators={formValue(values, "hasIncomeOther1") === "yes" ? requiredFieldDefault : []}
              onChange={handleChange}
              error={!!errors["hasIncomeOther2"]}
              helperText={errors["hasIncomeOther2"]}
              onBlur={handleBlur}
              disabled={formValue(values, "hasIncomeOther1") !== "yes"}
              registerValidators={registerValidators}
              dependentValidationFields={["incomeOther2Name", "incomeOther2Amount", "incomeOther2Period"]}
            >
              <MenuItem value={"no"}>Nein</MenuItem>
              <MenuItem value={"yes"}>Ja</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="incomeOther2Name"
              label="Beschreibung der Einnahmen"
              onBlur={handleBlur}
              registerValidators={registerValidators}
              validators={
                formValue(values, "hasIncomeOther2") === "yes" ? [...requiredFieldDefault, ...textFieldDefault] : []
              }
              error={!!errors["incomeOther2Name"]}
              helperText={errors["incomeOther2Name"]}
              margin="normal"
              value={formValue(values, "incomeOther2Name")}
              onChange={handleChange}
              disabled={
                formValue(values, "hasIncomeOther2") !== "yes" || formValue(values, "hasIncomeOther1") !== "yes"
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="incomeOther2Amount"
              name="incomeOther2Amount"
              label="Bruttobetrag"
              onBlur={handleBlur}
              registerValidators={registerValidators}
              isHighlighted={formValue(values, "hasIncomeOther2") === "yes"}
              error={!!errors["incomeOther2Amount"]}
              helperText={errors["incomeOther2Amount"]}
              margin="normal"
              value={formValue(values, "incomeOther2Amount")}
              onChange={handleChange}
              disabled={
                formValue(values, "hasIncomeOther2") !== "yes" || formValue(values, "hasIncomeOther1") !== "yes"
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Wann erhalten Sie die Einnahmen?"}
              fullWidth
              error={!!errors["incomeOther2Period"]}
              helperText={errors["incomeOther2Period"]}
              onBlur={handleBlur}
              value={formValue(values, "incomeOther2Period")}
              onChange={handleChange}
              name={`incomeOther2Period`}
              inputProps={{
                name: `incomeOther2Period`,
                id: "incomeOther2Period",
              }}
              registerValidators={registerValidators}
              validators={formValue(values, "hasIncomeOther2") === "yes" ? requiredFieldDefault : []}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { maxHeight: 200 },
                  },
                },
              }}
              disabled={
                formValue(values, "hasIncomeOther2") !== "yes" || formValue(values, "hasIncomeOther1") !== "yes"
              }
            >
              <MenuItem value={"jährlich"}>jährlich</MenuItem>
              <MenuItem value={"quartalsweise"}>quartalsweise</MenuItem>
              <MenuItem value={"monatlich"}>monatlich</MenuItem>
              <MenuItem value={"einmalig"}>einmalig</MenuItem>
              <MenuItem value={"unregelmäßig"}>unregelmäßig</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid size={12}>
            <SubmitButton
              error={_.keys(errors).length > 0}
              isLoading={isLoading}
              disabled={_.keys(touchedValues).length === 0}
              variant={"contained"}
              type={"submit"}
            >
              Änderungen speichern
            </SubmitButton>
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default IncomeOther;
